import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr } from "@/lib/utils-format";
import { Plus, Trash2, ChefHat, PackagePlus, AlertTriangle, Barcode, UploadCloud, ScanBarcode, Warehouse, Store, Truck, RefreshCw, Archive, ArchiveRestore, ShieldCheck, Pencil, SlidersHorizontal } from "lucide-react";
import { StockAdjustmentDialog } from "@/components/StockAdjustmentDialog";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { QuickAddCategory } from "@/components/quick-add";
import LabelPrintDialog from "@/components/LabelPrintDialog";
import { toast } from "sonner";

const DEFAULT_CATEGORIES = ["cake", "cookie", "bread", "pastry", "cupcake", "other"];
const UNITS = ["pcs", "kg", "g", "dozen", "box"];
const MOVEMENTS = ["purchase", "adjustment", "production", "wastage"];

const empty = { sku: "", name: "", category: "cake", unit: "pcs", barcode: "", ho_transfer_cost: "", mrp: 0, sale_price: 0, gst_rate: 5, low_stock_threshold: 0, description: "", active: true, available_for_b2b: false, b2b_bulk_tiers: [], mrp_mobile: "", mrp_online: "", mrp_wholesale: "", mfg_date: "", expiry_date: "" };
const emptyIngRow = { ingredient_id: "", quantity: 0, unit: "g" };
const emptyQuickPurchase = { enabled: false, vendor_id: "", quantity: 1, unit_price: 0, gst_rate: 5 };

const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const STOCK_STATUS = [
  { value: "any", label: "Any stock status" },
  { value: "low", label: "Low stock" },
  { value: "out", label: "Out of stock" },
];

// Returns { qty, threshold, status } for a product at a location ("all" | branch id)
const stockAt = (product, locationId) => {
  const globalThreshold = Number(product.low_stock_threshold || 0);
  if (locationId === "all") {
    const qty = Number(product.total_stock || 0);
    return { qty, threshold: globalThreshold, status: qty <= 0 ? "out" : qty <= globalThreshold ? "low" : "ok" };
  }
  const row = (product.stock_breakdown || []).find((r) => r.branch_id === locationId);
  const qty = Number(row?.quantity || 0);
  const threshold = Number(row?.low_threshold || 0) > 0 ? Number(row.low_threshold) : globalThreshold;
  return { qty, threshold, status: !row || qty <= 0 ? "out" : qty <= threshold ? "low" : "ok" };
};

const StockBreakdown = ({ product, locationId = "all", locationName }) => {
  const rows = product.stock_breakdown || [];
  const at = stockAt(product, locationId);
  const low = at.status !== "ok";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`product-stock-total-${product.id}`}
          title="View Warehouse vs Showroom breakdown"
          className={`text-right hover:text-[#C05621] ${low ? "text-red-600" : ""}`}
        >
          <span className="font-semibold underline decoration-dotted underline-offset-4">
            {fmtQty(at.qty)} {product.unit}
            {low && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}
          </span>
          {locationId !== "all" && (
            <div className="text-[10px] text-[#8B7355] font-normal" data-testid={`product-stock-location-note-${product.id}`}>
              at {locationName} · total {fmtQty(product.total_stock)}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-white p-0" data-testid={`product-stock-breakdown-${product.id}`}>
        <div className="px-4 py-3 border-b border-[#EAE5D9]">
          <div className="text-xs uppercase tracking-wide text-[#8B7355]">Stock across all locations</div>
          <div className="font-[Outfit] text-lg font-semibold">{fmtQty(product.total_stock)} {product.unit}</div>
          <div className="flex gap-4 mt-1 text-xs text-[#8B7355]">
            <span className="inline-flex items-center gap-1"><Warehouse className="w-3.5 h-3.5" /> Warehouse {fmtQty(product.warehouse_stock)}</span>
            <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5" /> Showrooms {fmtQty(product.showroom_stock)}</span>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-[#EAE5D9]">
          {rows.length === 0 && <div className="px-4 py-3 text-sm text-[#8B7355]">No stock recorded at any location yet.</div>}
          {rows.map((r) => (
            <div key={r.branch_id} className={`px-4 py-2 flex items-center justify-between text-sm ${r.branch_id === locationId ? "bg-[#FDF3EC]" : ""}`} data-testid={`stock-breakdown-row-${product.id}-${r.branch_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {r.branch_type === "warehouse" ? <Warehouse className="w-3.5 h-3.5 text-[#C05621] shrink-0" /> : <Store className="w-3.5 h-3.5 text-[#8B7355] shrink-0" />}
                <span className="truncate">{r.branch_name}</span>
                {r.branch_code && <span className="font-mono text-[10px] text-[#8B7355]">{r.branch_code}</span>}
              </div>
              <span className={`font-semibold tabular-nums ${r.quantity < 0 ? "text-red-600" : ""}`}>{fmtQty(r.quantity)}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBulkImport = ["super_admin", "admin"].includes(user?.role_id || user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [recipeFor, setRecipeFor] = useState(null); // product obj
  const [recipe, setRecipe] = useState({ yield_qty: 1, yield_unit: "pcs", ingredients: [{ ...emptyIngRow }], process_notes: "", labor_cost: 0, overhead_cost: 0, packaging_cost: 0 });
  const [ingredients, setIngredients] = useState([]);
  const [stockDialog, setStockDialog] = useState(null);
  const [labelProduct, setLabelProduct] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [movement, setMovement] = useState({ movement_type: "purchase", quantity: 0, unit_cost: 0, reference: "" });
  const [customCategories, setCustomCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [vendors, setVendors] = useState([]);
  const [quickPurchase, setQuickPurchase] = useState(emptyQuickPurchase);
  const [showArchived, setShowArchived] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("any");

  const load = async () => {
    const [p, i] = await Promise.allSettled([api.get(`/products${showArchived ? "?include_archived=true" : ""}`), api.get("/ingredients")]);
    if (p.status === "fulfilled") setItems(p.value.data); else toast.error(p.reason?.response?.data?.detail || "Failed to load products");
    setIngredients(i.status === "fulfilled" ? i.value.data : []);
    try {
      const s = await api.get("/settings");
      setCustomCategories((s.data?.product_categories) || []);
    } catch (_) {}
    try {
      const v = await api.get("/vendors");
      setVendors(v.data);
    } catch (_) {}
    try {
      const b = await api.get("/branches");
      const locs = (b.data || []).filter((x) => ["warehouse", "retail_showroom"].includes(x.type));
      locs.sort((a, c) => (a.type === "warehouse" ? -1 : c.type === "warehouse" ? 1 : (a.name || "").localeCompare(c.name || "")));
      setLocations(locs);
    } catch (_) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  const CATEGORIES = React.useMemo(() => Array.from(new Set([...DEFAULT_CATEGORIES, ...customCategories])), [customCategories]);
  const selectedLocation = locations.find((l) => l.id === locationFilter);
  const locationName = locationFilter === "all" ? "All locations" : selectedLocation?.type === "warehouse" ? "HO Warehouse" : (selectedLocation?.name || "location");
  const filteredItems = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (q && !((p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.barcode || "").toLowerCase().includes(q))) return false;
      if (statusFilter === "any") return true;
      const st = stockAt(p, locationFilter).status;
      return statusFilter === "low" ? st !== "ok" : st === "out";
    });
  }, [items, search, locationFilter, statusFilter]);

  const prepareDispatch = (p) => {
    const params = new URLSearchParams({ product_id: p.id });
    if (locationFilter !== "all" && selectedLocation?.type !== "warehouse") params.set("to_branch_id", locationFilter);
    navigate(`/app/warehouse-dispatch?${params.toString()}`);
  };


  const submit = async () => {
    if (!form.name || !form.sku) return toast.error("SKU & Name required");
    if (!editingId && (!form.mfg_date || !form.expiry_date)) return toast.error("Mfg Date & Expiry Date are required");
    if (quickPurchase.enabled && !quickPurchase.vendor_id) return toast.error("Select a vendor for the Quick Purchase");
    try {
      const payload = { ...form, mrp: Number(form.mrp) || 0, sale_price: Number(form.sale_price) || 0, gst_rate: Number(form.gst_rate) || 0, low_stock_threshold: Number(form.low_stock_threshold) || 0, available_for_b2b: !!form.available_for_b2b, b2b_bulk_tiers: (form.b2b_bulk_tiers || []).map((t) => ({ min_qty: Number(t.min_qty) || 0, discount_pct: Number(t.discount_pct) || 0 })), mrp_mobile: form.mrp_mobile === "" || form.mrp_mobile == null ? null : Number(form.mrp_mobile), mrp_online: form.mrp_online === "" || form.mrp_online == null ? null : Number(form.mrp_online), mrp_wholesale: form.mrp_wholesale === "" || form.mrp_wholesale == null ? null : Number(form.mrp_wholesale), ho_transfer_cost: form.ho_transfer_cost === "" || form.ho_transfer_cost == null ? null : Number(form.ho_transfer_cost) };
      let savedProduct = null;
      if (editingId) { await api.patch(`/products/${editingId}`, payload); }
      else { const { data } = await api.post("/products", payload); savedProduct = data; }
      if (!editingId && quickPurchase.enabled && savedProduct) {
        try {
          await api.post("/vendor-purchases", {
            vendor_id: quickPurchase.vendor_id,
            items: [{
              product_id: savedProduct.id, name: savedProduct.name, sku: savedProduct.sku,
              quantity: Number(quickPurchase.quantity) || 1, unit: savedProduct.unit,
              unit_price: Number(quickPurchase.unit_price) || 0, gst_rate: Number(quickPurchase.gst_rate) || 0,
              mfg_date: form.mfg_date, expiry_date: form.expiry_date,
            }],
          });
          toast.success(`${savedProduct.name} created — vendor purchase recorded & HO Warehouse stock credited`);
        } catch (e) {
          toast.error(`Product saved, but Quick Purchase failed: ${e?.response?.data?.detail || "error"}`);
        }
      } else {
        toast.success(editingId ? "Product updated" : "Product created");
      }
      setOpen(false); setForm(empty); setEditingId(null); setQuickPurchase(emptyQuickPurchase); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (p) => { const { stock_breakdown, current_stock, warehouse_stock, showroom_stock, has_recipe, recipe_cost, ...rest } = p; setForm({ ...empty, ...rest }); setEditingId(p.id); setQuickPurchase(emptyQuickPurchase); setOpen(true); };
  const archive = async (p, undo = false) => {
    try {
      await api.post(`/products/${p.id}/${undo ? "unarchive" : "archive"}`);
      toast.success(`${p.name} ${undo ? "restored" : "archived"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.can_archive) {
        toast.error(d.message, {
          duration: 12000,
          action: { label: "Archive instead", onClick: () => archive(p) },
        });
      } else toast.error((typeof d === "string" ? d : d?.message) || "Failed");
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every((p) => selectedIds.includes(p.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedIds((prev) => prev.filter((id) => !filteredItems.some((p) => p.id === id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredItems.map((p) => p.id)])));
  };
  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Permanently delete ${selectedIds.length} selected product(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/products/bulk-delete", { ids: selectedIds });
      const blocked = data.blocked || [];
      if (blocked.length) {
        toast.error(`${blocked.length} product(s) kept — still have stock/purchases/dispatches: ${blocked.slice(0, 3).map((b) => b.name).join(", ")}${blocked.length > 3 ? "…" : ""}`, {
          duration: 12000,
          action: { label: "Archive them", onClick: async () => {
            try { await api.post("/products/bulk-archive", { ids: blocked.map((b) => b.id) }); toast.success(`Archived ${blocked.length} product(s)`); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Archive failed"); }
          } },
        });
      }
      if (data.deleted_count) toast.success(`Deleted ${data.deleted_count} product(s)`);
      setSelectedIds([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };

  const [resyncing, setResyncing] = useState(false);
  const resyncStock = async () => {
    setResyncing(true);
    try {
      const { data } = await api.post("/inventory/resync-stock");
      toast.success(`Stock recalculated for ${data.products} product(s) — ${data.changed} corrected${data.repair?.rows_repointed ? `, ${data.repair.rows_repointed} orphaned stock row(s) re-linked (${data.repair.qty_relinked} units)` : ""}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resync failed"); }
    finally { setResyncing(false); }
  };

  const [ghostScan, setGhostScan] = useState(null);
  const [ghostBusy, setGhostBusy] = useState(false);
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const openGhostScan = async () => {
    try {
      const { data } = await api.get("/inventory/ghost-sales");
      if (!data.count) return toast.success("No ghost sales found — ledger and stock are clean");
      setGhostScan(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
  };
  const repairGhosts = async () => {
    setGhostBusy(true);
    try {
      const { data } = await api.post("/inventory/ghost-sales/repair");
      toast.success(`Reconciled ${data.journals_reversed} deleted sale(s) — ₹${Number(data.amount_reversed).toLocaleString("en-IN")} sales${data.payments_reversed ? ` + ₹${Number(data.payment_amount_reversed).toLocaleString("en-IN")} receipts` : ""} removed from ledger, ${data.qty_restored} unit(s) restored`);
      setGhostScan(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
    finally { setGhostBusy(false); }
  };
  const ghostEffects = ghostScan ? [
    `Post mirrored reversal entries for ${ghostScan.count} ghost sale journal(s) — removes ₹${Number(ghostScan.ghost_amount).toLocaleString("en-IN")} of phantom revenue/cash from the accounts`,
    ghostScan.payment_amount > 0 && `Reverse ₹${Number(ghostScan.payment_amount).toLocaleString("en-IN")} of linked customer receipts (cash/UPI) still sitting in the ledger and void those payment records`,
    ghostScan.restorable_qty > 0 && `Credit ${ghostScan.restorable_qty} unit(s) back to the showroom branches where those sales were made (from the deletion snapshot, or rebuilt from the original stock movements)`,
    ...ghostScan.ghosts.slice(0, 6).map((g) => `${g.number}: ₹${g.amount}${g.payment_amount ? ` + ₹${g.payment_amount} receipts` : ""}${g.branch_name ? ` · ${g.stock_qty} unit(s) → ${g.branch_name}${g.stock_source === "stock_movements" ? " (rebuilt from movements)" : ""}` : g.stock_already_restored ? " · stock already restored" : " · no snapshot or movements found, stock not restorable"}`),
    ghostScan.ghosts.length > 6 && `…and ${ghostScan.ghosts.length - 6} more`,
  ] : [];

  const bulkRestockActive = statusFilter !== "any" && locationFilter !== "all" && selectedLocation?.type === "retail_showroom";
  const selectedVisible = filteredItems.filter((p) => selectedIds.includes(p.id));
  const bulkRestockDispatch = () => {
    const wh = locations.find((l) => l.type === "warehouse");
    const whQty = (p) => Number((p.stock_breakdown || []).find((r) => r.branch_id === wh?.id)?.quantity || 0);
    const items = selectedVisible.map((p) => {
      const at = stockAt(p, locationFilter);
      const shortfall = Math.max(0, at.threshold - at.qty);
      return { product_id: p.id, quantity: Math.max(0, Math.min(shortfall, whQty(p))) };
    });
    const params = new URLSearchParams({ to_branch_id: locationFilter, items: JSON.stringify(items) });
    navigate(`/app/warehouse-dispatch?${params.toString()}`);
  };

  const openRecipe = async (p) => {
    setRecipeFor(p);
    const { data } = await api.get(`/products/${p.id}`);
    if (data.recipe) {
      setRecipe({
        yield_qty: data.recipe.yield_qty || 1,
        yield_unit: data.recipe.yield_unit || "pcs",
        ingredients: data.recipe.ingredients.map((i) => ({ ingredient_id: i.ingredient_id, quantity: i.quantity, unit: i.unit })),
        process_notes: data.recipe.process_notes || "",
        labor_cost: data.recipe.labor_cost || 0,
        overhead_cost: data.recipe.overhead_cost || 0,
        packaging_cost: data.recipe.packaging_cost || 0,
        _cached: data.recipe,
      });
    } else {
      setRecipe({ yield_qty: 1, yield_unit: "pcs", ingredients: [{ ...emptyIngRow }], process_notes: "", labor_cost: 0, overhead_cost: 0, packaging_cost: 0 });
    }
  };

  const saveRecipe = async () => {
    const valid = recipe.ingredients.filter((r) => r.ingredient_id && r.quantity > 0);
    if (valid.length === 0) return toast.error("Add at least one ingredient");
    try {
      const { data } = await api.put(`/products/${recipeFor.id}/recipe`, {
        yield_qty: Number(recipe.yield_qty) || 1,
        yield_unit: recipe.yield_unit,
        ingredients: valid.map((r) => ({ ingredient_id: r.ingredient_id, quantity: Number(r.quantity), unit: r.unit })),
        process_notes: recipe.process_notes,
        labor_cost: Number(recipe.labor_cost) || 0,
        overhead_cost: Number(recipe.overhead_cost) || 0,
        packaging_cost: Number(recipe.packaging_cost) || 0,
      });
      toast.success(`Recipe saved · Cost/unit ${data.cost_per_unit} · Margin ${data.margin_pct}%`);
      setRecipe({ ...recipe, _cached: data });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addIngRow = () => setRecipe((r) => ({ ...r, ingredients: [...r.ingredients, { ...emptyIngRow }] }));
  const removeIngRow = (i) => setRecipe((r) => ({ ...r, ingredients: r.ingredients.filter((_, x) => x !== i) }));
  const updateIngRow = (i, k, v) => setRecipe((r) => {
    const arr = [...r.ingredients];
    arr[i] = { ...arr[i], [k]: v };
    return { ...r, ingredients: arr };
  });

  // Live cost preview
  const previewIngCost = recipe.ingredients.reduce((sum, row) => {
    const ing = ingredients.find((x) => x.id === row.ingredient_id);
    if (!ing) return sum;
    return sum + Number(row.quantity || 0) * Number(ing.cost_per_unit || 0);
  }, 0);
  const previewProdCost = Number(recipe.labor_cost || 0) + Number(recipe.overhead_cost || 0) + Number(recipe.packaging_cost || 0);
  const previewCost = previewIngCost + previewProdCost;
  const previewPerUnit = previewCost / Math.max(Number(recipe.yield_qty) || 1, 1);
  const previewMargin = recipeFor ? (Number(recipeFor.sale_price) || 0) - previewPerUnit : 0;
  const previewMarginPct = recipeFor && recipeFor.sale_price ? (previewMargin / recipeFor.sale_price) * 100 : 0;

  const submitMovement = async () => {
    try {
      await api.post("/stock-movements", {
        item_type: "product",
        item_id: stockDialog.id,
        movement_type: movement.movement_type,
        quantity: Number(movement.quantity),
        unit_cost: Number(movement.unit_cost) || null,
        reference: movement.reference,
      });
      toast.success("Stock updated");
      setStockDialog(null);
      setMovement({ movement_type: "purchase", quantity: 0, unit_cost: 0, reference: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <ForceDeleteDialog open={!!ghostScan} onOpenChange={(o) => !o && setGhostScan(null)} title="Reconcile Deleted Sales" subject={`${ghostScan?.count || 0} ghost sale(s) worth ₹${Number(ghostScan?.ghost_amount || 0).toLocaleString("en-IN")}`}
        effects={ghostEffects} onConfirm={repairGhosts} busy={ghostBusy} testid="ghost-reconcile" verb="reconcile" actionNoun="repair" confirmLabel="Confirm Reconciliation" busyLabel="Reconciling…" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Catalog</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Products</h1>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={openGhostScan} title="Find deleted sales that left ledger money or missing stock behind, and fix them" data-testid="product-ghost-reconcile-btn">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Reconcile Deleted Sales
            </Button>
          )}
          {canBulkImport && (
            <Button
              variant="outline"
              onClick={resyncStock}
              disabled={resyncing}
              title="Recalculate every product's stock from all warehouse + showroom rows"
              data-testid="product-resync-stock-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${resyncing ? "animate-spin" : ""}`} /> Recalculate Stock
            </Button>
          )}
          {canBulkImport && (
            <Button
              variant="outline"
              onClick={() => navigate("/app/products/bulk-import")}
              data-testid="product-bulk-import-btn"
            >
              <UploadCloud className="w-4 h-4 mr-1.5" /> Bulk Import
            </Button>
          )}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); setQuickPurchase(emptyQuickPurchase); } }}>
            <DialogTrigger asChild>
              <Button data-testid="product-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> New Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">{editingId ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div><Label>SKU *</Label><Input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <div className="flex items-center gap-1.5">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <QuickAddCategory onAdded={async (cat) => { await load(); setForm((p) => ({ ...p, category: cat })); }} />
                </div>
              </div>
              <div className="col-span-2"><Label>Name *</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="col-span-2">
                <Label className="flex items-center gap-1"><Barcode className="w-3.5 h-3.5" /> Barcode</Label>
                <Input data-testid="product-barcode-input" placeholder="Scan or type barcode…" value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
              </div>
              <div>
                <Label>HO Transfer Rate (₹)</Label>
                <Input data-testid="product-ho-transfer-cost-input" type="number" min="0" step="0.01" placeholder="HO supply price to showrooms" value={form.ho_transfer_cost ?? ""} onChange={(e) => setForm({ ...form, ho_transfer_cost: e.target.value })} />
                <p className="text-[10px] text-[#8B7355] mt-1">Used to value HO → Showroom dispatches in the ledger</p>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>GST %</Label><Input type="number" data-testid="product-gst-input" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} /></div>
              <div><Label>MRP (₹)</Label><Input type="number" data-testid="product-mrp-input" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></div>
              <div><Label>Sale Price (₹)</Label><Input type="number" data-testid="product-sale-price-input" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></div>
              <div className="col-span-2 border-t pt-2 mt-1">
                <div className="uc-label mb-2 !text-[#5C4033]">Channel Price Lists (optional)</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <Label className="text-[10px]">Mobile App MRP (₹)</Label>
                    <Input type="number" min="0" step="0.5" placeholder={String(form.mrp || 0)}
                      value={form.mrp_mobile ?? ""}
                      onChange={(e) => setForm({ ...form, mrp_mobile: e.target.value })}
                      data-testid="product-form-mrp-mobile" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Online Store MRP (₹)</Label>
                    <Input type="number" min="0" step="0.5" placeholder={String(form.mrp || 0)}
                      value={form.mrp_online ?? ""}
                      onChange={(e) => setForm({ ...form, mrp_online: e.target.value })}
                      data-testid="product-form-mrp-online" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Wholesale MRP (₹)</Label>
                    <Input type="number" min="0" step="0.5" placeholder={String(form.mrp || 0)}
                      value={form.mrp_wholesale ?? ""}
                      onChange={(e) => setForm({ ...form, mrp_wholesale: e.target.value })}
                      data-testid="product-form-mrp-wholesale" />
                  </div>
                </div>
                <div className="text-[10px] text-[#8B7355] mt-1">
                  Leave empty to reuse the base MRP. Per-showroom retail prices can be set from Retail Showrooms → Catalog.
                </div>
              </div>
              <div>
                <Label>Stock (all locations)</Label>
                <div className="h-10 flex items-center text-sm text-[#8B7355]" data-testid="product-form-stock-readonly">
                  {editingId ? `${fmtQty(form.total_stock)} ${form.unit} · computed live from Warehouse + Showrooms` : "Set via Quick Purchase below or a Stock movement after saving"}
                </div>
              </div>
              <div><Label>Low-stock at</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
              <div>
                <Label>Mfg Date {!editingId && "*"}</Label>
                <Input type="date" data-testid="product-mfg-date-input" value={form.mfg_date || ""} onChange={(e) => setForm({ ...form, mfg_date: e.target.value })} />
              </div>
              <div>
                <Label>Expiry Date {!editingId && "*"}</Label>
                <Input type="date" data-testid="product-expiry-date-input" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
              </div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
              {!editingId && (
                <div className="col-span-2 border-t border-[#EAE5D9] pt-3">
                  <div className="flex items-center gap-3">
                    <Switch checked={quickPurchase.enabled} onCheckedChange={(v) => setQuickPurchase({ ...quickPurchase, enabled: v })} data-testid="quick-purchase-switch" />
                    <Label>Also record a Vendor Purchase for this product now</Label>
                  </div>
                  {quickPurchase.enabled && (
                    <div className="mt-3 bg-[#F7F2E8] rounded-md p-3 space-y-3">
                      <div className="text-xs text-[#8B7355]">Creates a Vendor Purchase using the Mfg/Expiry Date above — instantly credits HO Warehouse stock.</div>
                      <div>
                        <Label>Vendor *</Label>
                        <Select value={quickPurchase.vendor_id} onValueChange={(v) => setQuickPurchase({ ...quickPurchase, vendor_id: v })}>
                          <SelectTrigger data-testid="quick-purchase-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><Label className="text-[10px]">Qty</Label><Input type="number" value={quickPurchase.quantity} onChange={(e) => setQuickPurchase({ ...quickPurchase, quantity: e.target.value })} data-testid="quick-purchase-qty-input" /></div>
                        <div><Label className="text-[10px]">Purchase Price (₹)</Label><Input type="number" value={quickPurchase.unit_price} onChange={(e) => setQuickPurchase({ ...quickPurchase, unit_price: e.target.value })} data-testid="quick-purchase-price-input" /></div>
                        <div><Label className="text-[10px]">GST %</Label><Input type="number" value={quickPurchase.gst_rate} onChange={(e) => setQuickPurchase({ ...quickPurchase, gst_rate: e.target.value })} /></div>
                      </div>
                      {vendors.length === 0 && <div className="text-xs text-red-600">No vendors yet — add one from Vendor Purchases first.</div>}
                    </div>
                  )}
                </div>
              )}
              <div className="col-span-2 border-t border-[#EAE5D9] pt-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={!!form.available_for_b2b}
                    onCheckedChange={(v) => setForm({ ...form, available_for_b2b: v })}
                    data-testid="product-b2b-switch"
                  />
                  <Label>Available on B2B / wholesale portal</Label>
                </div>
                {form.available_for_b2b && (
                  <div className="mt-3">
                    <Label className="flex items-center justify-between">
                      <span>Bulk pricing tiers</span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, b2b_bulk_tiers: [...(form.b2b_bulk_tiers || []), { min_qty: 10, discount_pct: 5 }] })}
                        className="text-xs text-[#C05621] hover:underline"
                        data-testid="product-bulk-add"
                      >+ add tier</button>
                    </Label>
                    <div className="mt-2 space-y-2">
                      {(form.b2b_bulk_tiers || []).map((t, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                          <div>
                            <div className="text-[10px] text-[#8B7355]">Min qty</div>
                            <Input type="number" value={t.min_qty} onChange={(e) => setForm({ ...form, b2b_bulk_tiers: form.b2b_bulk_tiers.map((x, idx) => idx === i ? { ...x, min_qty: Number(e.target.value) } : x) })} data-testid={`product-bulk-qty-${i}`} />
                          </div>
                          <div>
                            <div className="text-[10px] text-[#8B7355]">Discount %</div>
                            <Input type="number" step="0.1" value={t.discount_pct} onChange={(e) => setForm({ ...form, b2b_bulk_tiers: form.b2b_bulk_tiers.map((x, idx) => idx === i ? { ...x, discount_pct: Number(e.target.value) } : x) })} data-testid={`product-bulk-pct-${i}`} />
                          </div>
                          <button type="button" onClick={() => setForm({ ...form, b2b_bulk_tiers: form.b2b_bulk_tiers.filter((_, idx) => idx !== i) })} className="text-xs text-red-600 pt-3" data-testid={`product-bulk-remove-${i}`}>remove</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="product-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <ScanBarcode className="w-4 h-4 text-[#8B7355] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Scan barcode or search by name / SKU…"
            className="pl-9"
            data-testid="product-search-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-56 bg-white" data-testid="product-location-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" data-testid="product-location-option-all">All locations</SelectItem>
              {locations.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`product-location-option-${l.id}`}>
                  {l.type === "warehouse" ? `HO Warehouse (${l.name})` : l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-white" data-testid="product-stock-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {STOCK_STATUS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`product-stock-status-option-${s.value}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(locationFilter !== "all" || statusFilter !== "any") && (
            <button onClick={() => { setLocationFilter("all"); setStatusFilter("any"); }} className="text-xs text-[#C05621] hover:underline" data-testid="product-clear-filters">Clear</button>
          )}
          <label className="flex items-center gap-2 text-xs text-[#8B7355] ml-1 cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="product-show-archived-toggle" /> Show archived
          </label>
        </div>
        {bulkRestockActive && selectedVisible.length > 0 && (
          <Button
            onClick={bulkRestockDispatch}
            data-testid="product-bulk-dispatch-btn"
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
          >
            <Truck className="w-4 h-4 mr-1.5" /> Dispatch {selectedVisible.length} selected to {selectedLocation?.name}
          </Button>
        )}
        {selectedIds.length > 0 && (
          <Button
            variant="destructive"
            onClick={deleteSelected}
            data-testid="product-delete-selected-btn"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  data-testid="product-select-all-checkbox"
                />
              </th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Barcode</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">MRP</th>
              <th className="px-4 py-3 text-right">Sale</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3">Recipe</th>
              <th className="px-4 py-3"></th>
            </tr>

          </thead>
          <tbody>
            {filteredItems.map((p) => {
              return (
                <tr key={p.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`product-select-checkbox-${p.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => isSuperAdmin ? openEdit(p) : setStockDialog(p)}>
                    <span className={p.archived ? "text-[#8B7355] line-through" : ""}>{p.name}</span>
                    {p.archived && <Badge variant="outline" className="ml-2 bg-stone-100 text-stone-600 border-stone-300 text-[10px]" data-testid={`product-archived-badge-${p.id}`}>Archived</Badge>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#8B7355]">{p.barcode || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{p.category}</Badge></td>
                  <td className="px-4 py-3 text-right">{inr(p.mrp)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#C05621]">{inr(p.sale_price)}</td>
                  <td className="px-4 py-3 text-right">
                    <StockBreakdown product={p} locationId={locationFilter} locationName={locationName} />
                  </td>
                  <td className="px-4 py-3">
                    {p.has_recipe ? (
                      <span className="text-xs">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Cost {inr(p.recipe_cost || 0)}</Badge>
                      </span>
                    ) : <span className="text-xs text-[#8B7355]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1 items-center">
                      {statusFilter !== "any" && (
                        <button onClick={() => prepareDispatch(p)} data-testid={`product-prepare-dispatch-btn-${p.id}`} title="Prepare Dispatch" className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#C05621] text-white hover:bg-[#9C4221]">
                          <Truck className="w-3.5 h-3.5" /> Dispatch
                        </button>
                      )}
                      <button onClick={() => openRecipe(p)} data-testid={`product-recipe-btn-${p.id}`} title="Recipe" className="p-1.5 hover:bg-[#F7F2E8] rounded text-[#C05621]"><ChefHat className="w-4 h-4" /></button>
                      <button onClick={() => setStockDialog(p)} data-testid={`product-stock-btn-${p.id}`} title="Stock" className="p-1.5 hover:bg-[#F7F2E8] rounded"><PackagePlus className="w-4 h-4" /></button>
                      <button onClick={() => setLabelProduct(p)} data-testid={`product-label-btn-${p.id}`} title="Print labels" className="p-1.5 hover:bg-[#F7F2E8] rounded text-[#2C1810]"><Barcode className="w-4 h-4" /></button>
                      {isSuperAdmin && (
                        <>
                          <button onClick={() => setAdjusting(p)} data-testid={`product-adjust-stock-btn-${p.id}`} title="Adjust stock quantity (Super Admin)" className="p-1.5 hover:bg-orange-50 text-orange-700 rounded"><SlidersHorizontal className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(p)} data-testid={`product-edit-btn-${p.id}`} title="Edit (Super Admin)" className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>
                          {p.archived
                            ? <button onClick={() => archive(p, true)} data-testid={`product-unarchive-btn-${p.id}`} title="Restore from archive" className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded"><ArchiveRestore className="w-4 h-4" /></button>
                            : <button onClick={() => archive(p)} data-testid={`product-archive-btn-${p.id}`} title="Archive (hide from lists, keep history)" className="p-1.5 hover:bg-stone-100 text-stone-600 rounded"><Archive className="w-4 h-4" /></button>}
                          <button onClick={() => remove(p)} data-testid={`product-delete-btn-${p.id}`} title="Delete (only when no stock or history)" className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#8B7355]">No products yet.</td></tr>
            )}
            {items.length > 0 && filteredItems.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#8B7355]" data-testid="product-filter-empty">No products match — {STOCK_STATUS.find((s) => s.value === statusFilter)?.label.toLowerCase()} at {locationName}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recipe dialog */}
      <Dialog open={!!recipeFor} onOpenChange={(o) => !o && setRecipeFor(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2"><ChefHat className="w-5 h-5 text-[#C05621]" /> Recipe · {recipeFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yield Quantity</Label><Input type="number" value={recipe.yield_qty} onChange={(e) => setRecipe({ ...recipe, yield_qty: e.target.value })} data-testid="recipe-yield-input" /></div>
              <div>
                <Label>Yield Unit</Label>
                <Select value={recipe.yield_unit} onValueChange={(v) => setRecipe({ ...recipe, yield_unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="uc-label">Ingredients (BOM)</div>
                <Button size="sm" variant="outline" onClick={addIngRow} data-testid="recipe-add-ing"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F2E8] uc-label">
                    <tr>
                      <th className="text-left px-2 py-2">Ingredient</th>
                      <th className="text-right px-2 py-2 w-24">Qty</th>
                      <th className="text-left px-2 py-2 w-24">Unit</th>
                      <th className="text-right px-2 py-2 w-28">Cost</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.ingredients.map((row, i) => {
                      const ing = ingredients.find((x) => x.id === row.ingredient_id);
                      const cost = ing ? Number(ing.cost_per_unit) * Number(row.quantity || 0) : 0;
                      return (
                        <tr key={i} className="border-t border-[#EAE5D9]">
                          <td className="px-2 py-1.5">
                            <Select value={row.ingredient_id} onValueChange={(v) => updateIngRow(i, "ingredient_id", v)}>
                              <SelectTrigger className="h-8" data-testid={`recipe-ing-select-${i}`}><SelectValue placeholder="Select ingredient" /></SelectTrigger>
                              <SelectContent className="bg-white max-h-64">
                                {ingredients.map((ing) => <SelectItem key={ing.id} value={ing.id}>{ing.name} · {inr(ing.cost_per_unit)}/{ing.unit}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5"><Input type="number" value={row.quantity} onChange={(e) => updateIngRow(i, "quantity", e.target.value)} className="h-8 text-right" /></td>
                          <td className="px-2 py-1.5">
                            <Select value={row.unit} onValueChange={(v) => updateIngRow(i, "unit", v)}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5 text-right font-medium">{inr(cost)}</td>
                          <td className="px-2 py-1.5">
                            {recipe.ingredients.length > 1 && (
                              <button onClick={() => removeIngRow(i)} className="text-red-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div><Label>Process Notes</Label><Textarea rows={3} value={recipe.process_notes || ""} onChange={(e) => setRecipe({ ...recipe, process_notes: e.target.value })} placeholder="Mixing, baking temperature & time, decoration steps…" /></div>

            <div>
              <div className="uc-label mb-2">Production Costs (per batch)</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Labor (₹)</Label><Input type="number" value={recipe.labor_cost} onChange={(e) => setRecipe({ ...recipe, labor_cost: e.target.value })} data-testid="recipe-labor-input" /></div>
                <div><Label>Overhead (₹)</Label><Input type="number" value={recipe.overhead_cost} onChange={(e) => setRecipe({ ...recipe, overhead_cost: e.target.value })} data-testid="recipe-overhead-input" /></div>
                <div><Label>Packaging (₹)</Label><Input type="number" value={recipe.packaging_cost} onChange={(e) => setRecipe({ ...recipe, packaging_cost: e.target.value })} data-testid="recipe-packaging-input" /></div>
              </div>
            </div>

            <div className="bg-[#F7F2E8] rounded-md p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="uc-label">Ingredient Cost</div><div className="font-[Outfit] font-semibold text-base">{inr(previewIngCost)}</div></div>
              <div><div className="uc-label">Production Cost</div><div className="font-[Outfit] font-semibold text-base">{inr(previewProdCost)}</div></div>
              <div><div className="uc-label">Total Cost (batch)</div><div className="font-[Outfit] font-semibold text-base">{inr(previewCost)}</div></div>
              <div><div className="uc-label">Cost per Unit</div><div className="font-[Outfit] font-semibold text-lg text-[#C05621]">{inr(previewPerUnit)}</div></div>
              <div><div className="uc-label">Sale Price</div><div className="font-[Outfit] font-semibold text-base">{inr(recipeFor?.sale_price || 0)}</div></div>
              <div className="col-span-2 md:col-span-3">
                <div className="uc-label">Margin per Unit</div>
                <div className={`font-[Outfit] font-semibold text-lg ${previewMargin >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {inr(previewMargin)} · {previewMarginPct.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipeFor(null)}>Close</Button>
            <Button onClick={saveRecipe} data-testid="recipe-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save Recipe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock dialog */}
      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">Stock — {stockDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Type</Label>
              <Select value={movement.movement_type} onValueChange={(v) => setMovement({ ...movement, movement_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{MOVEMENTS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantity ({stockDialog?.unit})</Label><Input type="number" value={movement.quantity} onChange={(e) => setMovement({ ...movement, quantity: e.target.value })} /></div>
            <div><Label>Unit Cost (₹)</Label><Input type="number" value={movement.unit_cost} onChange={(e) => setMovement({ ...movement, unit_cost: e.target.value })} /></div>
            <div><Label>Reference</Label><Input value={movement.reference} onChange={(e) => setMovement({ ...movement, reference: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialog(null)}>Cancel</Button>
            <Button onClick={submitMovement} className="bg-[#C05621] hover:bg-[#9C4221] text-white">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LabelPrintDialog open={!!labelProduct} onOpenChange={(v) => !v && setLabelProduct(null)} product={labelProduct} />
      {adjusting && <StockAdjustmentDialog product={adjusting} onClose={() => setAdjusting(null)} onDone={load} />}
    </div>
  );
}
